// Search boxes that can add what is missing (docs/plans/add-here.md): which
// rows match what was typed, and when "Add … as new" may be offered.

export interface Named {
  name: string
  phone?: string | null
}

/** A name as people compare them: case and extra spaces do not make a new one. */
export function nameKey(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLowerCase()
}

export function sameName(a: string, b: string): boolean {
  return nameKey(a) === nameKey(b)
}

/** Rows whose name or phone has the typed text in it; all of them for no text. */
export function matchParties<T extends Named>(rows: readonly T[], text: string, limit = 8): T[] {
  const term = nameKey(text)
  const hits = term
    ? rows.filter(
        (r) => nameKey(r.name).includes(term) || (r.phone ?? '').toLowerCase().includes(term),
      )
    : rows
  return hits.slice(0, limit)
}

/** The row that already has this name, if any. */
export function findSameName<T extends { name: string }>(
  rows: readonly T[],
  text: string,
): T | null {
  const key = nameKey(text)
  return key ? (rows.find((r) => nameKey(r.name) === key) ?? null) : null
}

/** "Add … as new" is offered for a typed name nobody has yet. */
export function canOfferNew(rows: readonly { name: string }[], text: string): boolean {
  return nameKey(text) !== '' && !findSameName(rows, text)
}

/** Typed text that is a code, not a name: it starts a new product's barcode. */
export function looksLikeCode(text: string): boolean {
  return /^\d{6,}$/.test(text.trim())
}
